import { useEffect, useReducer, useRef } from 'react';
import Button from 'components/ui-components/Button';
import ManualAddressForm from './manual_address_form';
import useErrorPush from '../../utils/useErrorPush';
import scrollElementIntoView from '../../utils/scrollIntoView';
import { reducer, initialState } from './reducers/new_address_reducer';

const NewAddress = ({
  initialShowManualAddressForm,
  addressMode,
  addressEnquiryUrl,
  currentStep,
  configGenericError,
  deliveryAddressUrl,
  getAddressBook,
  handleGuestBillingAddress,
  handleTelephoneError,
  method,
  progressToCardDetails,
  setAddressFromAddressBook,
  setAddressMode,
  signedIn,
  telephone,
  toggleGenericError,
  handleSignedInpostcodeError,
  guestName,
  guestPostcodeError,
  hasValidGuestContactDetails,
}) => {
  const addressRef = useRef();
  const deliveryScrollRef = useRef();

  const [
    {
      showManualAddressForm,
      searchPostcode,
      postcodeValid,
      searchPostcodeError,
      addresses,
      newAddress,
      newAddressValid,
      nameEmpty,
      loading,
      showManualAddressButton,
      postcodeErrorMessage,
      postcodeExcluded,
      disableContinueButton,
    },
    dispatch,
  ] = useReducer(reducer, { ...initialState });

  useEffect(
    () =>
      dispatch({
        type: 'SET_SHOW_MANUAL_ADDRESS_FORM',
        payload: initialShowManualAddressForm,
      }),
    [initialShowManualAddressForm],
  );

  useEffect(() => {
    dispatch({
      type: 'SET_ADDRESS',
      payload: { ...newAddress, name: guestName },
    });
  }, [guestName]);

  const handleNameChange = e => {
    dispatch({
      type: 'SET_ADDRESS',
      payload: { ...newAddress, name: e.target.value },
    });
  };

  const globalHandleInputChange = e => {
    const value = e.target.value;
    const name = e.target.name;

    dispatch({
      type: 'SET_ADDRESS',
      payload: { ...newAddress, [name]: value },
    });
  };

  const handleSearchPostCodeChange = e => {
    dispatch({ type: 'SET_SEARCH_POSTCODE', payload: e.target.value });
  };

  const searchForAddressByPostcode = () => {
    if (newAddress.name === '') {
      dispatch({ type: 'SET_NAME_EMPTY', payload: true });
      useErrorPush('checkout: address name empty', window.dataLayer);
      return;
    }

    dispatch({ type: 'CLEAR_POSTCODE_ADDRESSES' });

    if (postcodeValid) {
      $.ajax({
        url: addressEnquiryUrl,
        method: 'GET',
        dataType: 'json',
        data: {
          postcode: searchPostcode,
        },
        success: data => {
          dispatch({ type: 'SET_POSTCODE_ADDRESSES', payload: data });
          handleSignedInpostcodeError(false);
          scrollElementIntoView(addressRef);
        },
        error: ({ responseText }) => {
          const errorMessage = JSON.parse(responseText).message;
          const excluded = JSON.parse(responseText).postcode_excluded;

          dispatch({
            type: 'SET_POSTCODE_LOOKUP_ERROR',
            payload: { errorMessage, excluded },
          });

          useErrorPush(postcodeErrorMessage, window.dataLayer);
          handleSignedInpostcodeError(true);
          scrollElementIntoView(addressRef);
        },
      });
    }
  };

  const createAddress = () => {
    if (
      hasValidGuestContactDetails !== undefined &&
      !hasValidGuestContactDetails()
    )
      return;

    let data = {};
    if (currentStep === 'PAYMENT') {
      data = {
        billing_name: newAddress.name,
        billing_address_1: newAddress.line_1,
        billing_address_2: newAddress.line_2,
        billing_address_3: newAddress.line_3,
        billing_address_4: newAddress.line_4,
        billing_address_5: newAddress.line_5,
        billing_postcode: newAddress.postcode,
      };
    } else {
      data = {
        new_address: {
          name: newAddress.name,
          line_1: newAddress.line_1,
          line_2: newAddress.line_2,
          line_3: newAddress.line_3,
          line_4: newAddress.line_4,
          line_5: newAddress.line_5,
          postcode: newAddress.postcode,
        },
      };
    }

    if (handleGuestBillingAddress) {
      handleGuestBillingAddress(newAddress);
    } else if (!signedIn && telephone === '') {
      handleTelephoneError(true);
    } else {
      dispatch({ type: 'SET_DISABLE_CONTINUE_BUTTON', payload: true });

      $.ajax({
        url: deliveryAddressUrl,
        method: method || 'POST',
        dataType: 'json',
        data,
        success: () => {
          dispatch({ type: 'SET_DISABLE_CONTINUE_BUTTON', payload: false });

          if (signedIn) {
            getAddressBook();
            setAddressFromAddressBook(newAddress);
            setAddressMode('ADDRESS_BOOK');
            dispatch({ type: 'CLEAR_ADDRESS_PANEL' });
            if (currentStep === 'PAYMENT') {
              progressToCardDetails();
            }
          } else {
            setAddressFromAddressBook({
              ...newAddress,
              name: guestName,
            });
            setAddressMode('ADDRESS_BOOK');
            dispatch({ type: 'CLEAR_ADDRESS_PANEL' });
            if (currentStep === 'PAYMENT') {
              progressToCardDetails();
            }
          }
        },
        error: () => {
          dispatch({ type: 'SET_DISABLE_CONTINUE_BUTTON', payload: false });
          configGenericError({
            title: 'Error creating address',
            body: (
              <div>
                <p>
                  Unfortunately there was an error creating your new address.
                </p>
                <p>
                  We are sorry for any inconvenience this may cause. Please try
                  again later. If the problem persists, please contact our
                  customer service team.
                </p>
              </div>
            ),
            errorButton: true,
            errorButtonTitle: 'Close',
            errorButtonAction: toggleGenericError,
            closeAction: toggleGenericError,
          });
        },
      });
    }
  };

  const fillAddressForm = index => {
    dispatch({ type: 'SET_ADDRESS_FIELDS', payload: addresses[index] });
    scrollElementIntoView(deliveryScrollRef);
  };

  return (
    <div
      className={`checkout-sub-panel find-address ${
        addressMode !== 'NEW_ADDRESS' ? 'hide' : ''
      }`}>
      <div className="new-address">
        <h2>Enter a new Address</h2>
        {!guestPostcodeError && (
          <div>
            <div className="input-address">
              <div className="manual-address-wrapper">
                <div className="fl-fb-50">
                  <label htmlFor="input-name">
                    Name <strong className="required">*</strong>
                  </label>
                  <input
                    onChange={handleNameChange}
                    type="text"
                    value={newAddress.name}
                    autoComplete="off"
                    name="input-name"
                    id="input-name"
                  />
                  {nameEmpty && (
                    <p>
                      <span className="search-postcode--error">
                        Please enter a name for the address.
                      </span>
                    </p>
                  )}

                  <label ref={addressRef} htmlFor="postcode-search">
                    Please enter your postcode{' '}
                    <strong className="required">*</strong>
                  </label>
                  <div className="fl fw-w ai-c">
                    <input
                      type="text"
                      value={searchPostcode}
                      onChange={handleSearchPostCodeChange}
                      autoComplete="off"
                      name="postcode-search"
                      id="postcode-search"
                      className="new-address-postcode-search"
                    />
                    <button
                      className="find-address-button fl-1"
                      onClick={searchForAddressByPostcode}
                      disabled={!postcodeValid}
                      type="button"
                      data-testid={
                        !signedIn
                          ? 'guest-find-address'
                          : 'account-find-address'
                      }>
                      {loading ? (
                        <span className="loading">
                          <i className="icon-spinner icon-spin icon-large" />
                        </span>
                      ) : (
                        'Find Address'
                      )}
                    </button>
                    {showManualAddressButton && !postcodeExcluded && (
                      <div className="manual-select-wrapper">
                        <button
                          className="manual-select"
                          onClick={() =>
                            dispatch({
                              type: 'SET_SHOW_MANUAL_ADDRESS_FORM',
                              payload: true,
                            })
                          }
                          type="button">
                          Enter Address Manually
                        </button>
                      </div>
                    )}
                  </div>
                </div>
              </div>
              {searchPostcodeError && (
                <p>
                  <span className="search-postcode--error">
                    {postcodeErrorMessage}
                  </span>
                </p>
              )}
            </div>
            {addresses.length > 0 && (
              <div className="available--addresses">
                <ul>
                  {addresses.map((address, index) => (
                    <li
                      key={address.short_name}
                      onClick={() => {
                        fillAddressForm(index);
                      }}>
                      {address.short_name}
                    </li>
                  ))}
                </ul>
              </div>
            )}
          </div>
        )}
      </div>
      {showManualAddressForm && !postcodeExcluded && (
        <div>
          <ManualAddressForm
            handleAddressLine1Change={globalHandleInputChange}
            handleAddressLine2Change={globalHandleInputChange}
            handleAddressLine3Change={globalHandleInputChange}
            handleAddressLine4Change={globalHandleInputChange}
            handlePostCodeChange={globalHandleInputChange}
            line1={newAddress.line_1}
            line2={newAddress.line_2}
            line3={newAddress.line_3}
            line4={newAddress.line_4}
            postcode={newAddress.postcode}
          />

          <span ref={deliveryScrollRef}>
            <Button
              className="cef-btn cef-btn--green ff-body"
              handleClick={createAddress}
              disabled={!newAddressValid || disableContinueButton}
              testId="set-address">
              {disableContinueButton ? (
                <div className="loader" />
              ) : currentStep !== 'PAYMENT' ? (
                'Continue to Delivery Details'
              ) : (
                'Set Address'
              )}
            </Button>
          </span>
        </div>
      )}
    </div>
  );
};

export default NewAddress;
